/**
 * API Client for One Voice Backend
 *
 * This module provides a type-safe HTTP client for communicating with the
 * One Voice FastAPI backend. It handles:
 *   - JWT token management (storage, retrieval, auto-injection)
 *   - Automatic 401 handling with redirect to login
 *   - Type-safe request/response handling
 *   - Error normalization
 *
 * Usage:
 *   import { api } from '@/lib/api';
 *
 *   // GET request
 *   const { data, error } = await api.get<User>('/auth/me');
 *
 *   // POST request
 *   const { data, error } = await api.post<PrayerRequest>('/prayer-requests', {
 *     body: 'Please pray for...',
 *     type: 'prayer_request'
 *   });
 *
 * Authentication:
 *   - Tokens are stored in localStorage under 'access_token'
 *   - All requests include the token by default (auth: true)
 *   - Set auth: false for public endpoints (login, register)
 *   - 401 responses automatically clear token and redirect to /login
 *
 * @module lib/api
 */

import { logger } from "@/lib/logger";

// Flag to prevent redirect loops during auth operations
let isAuthOperationInProgress = false;
let isAuthLoading = false;

/**
 * Set flag to prevent 401 redirects during auth operations
 * @internal
 */
export function setAuthOperationInProgress(value: boolean): void {
  isAuthOperationInProgress = value;
}

/**
 * Set flag to indicate auth is loading (prevents redirects during initial load)
 * @internal
 */
export function setAuthLoading(value: boolean): void {
  isAuthLoading = value;
}

/**
 * Returns the backend API base URL. Resolved at request time so that:
 * - In development (browser), always uses "/api" (relative) so requests
 *   route through the Vite dev proxy. This avoids CSP connect-src
 *   violations when accessing the app via LAN IP on mobile devices.
 *   Override with ?api=localhost to bypass the proxy.
 * - In production, uses VITE_API_URL or hostname-based URL.
 */
export function getApiBaseUrl(): string {
  // In development browser context, route through the Vite proxy.
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const params = window.location?.search ? new URLSearchParams(window.location.search) : null;
    if (params?.get("api") === "localhost") {
      return "http://localhost:8000/api";
    }
    return "/api";
  }
  const fromEnv = import.meta.env.VITE_API_URL;
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined" && window.location?.hostname) {
    const host = window.location.hostname;
    if (host !== "localhost" && host !== "127.0.0.1") {
      return `http://${host}:8000/api`;
    }
  }
  return "http://localhost:8000/api";
}

/**
 * Absolute URL for Clearstream inbound SMS webhooks.
 * Clearstream must POST to the backend directly (not a relative /api path).
 */
export function getClearstreamWebhookUrl(): string {
  const base = getApiBaseUrl().replace(/\/$/, "");
  const absoluteBase = base.startsWith("/") ? "http://localhost:8000/api" : base;
  return `${absoluteBase}/webhooks/clearstream`;
}

/**
 * Absolute URL for a production RSS feed path (e.g. ProPresenter).
 * Relative `/api` from local Vite proxy becomes `http://localhost:8000/api`.
 *
 * @param path - Path under `/api/production/`, with or without a leading slash
 *   (e.g. `live-prayer-request/body`).
 */
export function getProductionRssUrl(path: string): string {
  const base = getApiBaseUrl().replace(/\/$/, "");
  const absoluteBase = base.startsWith("/") ? "http://localhost:8000/api" : base;
  const normalized = path.replace(/^\//, "");
  return `${absoluteBase}/production/${normalized}`;
}

/** Supported HTTP methods for the API client */
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

/**
 * Options for API requests.
 *
 * @property method - HTTP method (defaults to GET)
 * @property body - Request body for POST/PUT/PATCH requests
 * @property headers - Additional headers to include
 * @property auth - Whether to include auth token (defaults to true)
 * @property signal - Optional AbortSignal to cancel the request
 */
interface RequestOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  auth?: boolean;
  signal?: AbortSignal;
}

/**
 * Standardized API response wrapper.
 *
 * @template T - Type of the response data
 * @property data - Parsed response data (null on error)
 * @property error - Error message (null on success)
 * @property status - HTTP status code (0 for network errors)
 */
interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  status: number;
  /** Selected response headers (e.g. X-Views-Today from public prayer endpoints). */
  responseHeaders?: Record<string, string>;
}

function pickRelevantResponseHeaders(response: Response): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  const xViews = response.headers.get("X-Views-Today");
  if (xViews !== null && xViews !== "") {
    out["X-Views-Today"] = xViews;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * Transforms snake_case object keys to camelCase recursively.
 * Handles nested objects and arrays.
 * 
 * This function is idempotent - calling it multiple times won't change the result.
 */
export function transformSnakeToCamel(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== "object") return obj;
  if (Array.isArray(obj)) {
    return obj.map(transformSnakeToCamel);
  }
  // Don't transform Date objects or other special objects
  if (obj instanceof Date) return obj;
  
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    // Convert snake_case to camelCase
    // Pattern: _[a-z] -> uppercase the letter
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    result[camelKey] = transformSnakeToCamel(value);
  }
  return result;
}

/**
 * Transforms camelCase object keys to snake_case recursively.
 * Handles nested objects and arrays.
 * 
 * This function converts camelCase to snake_case. If a key already contains
 * underscores, it's assumed to already be in snake_case and is left unchanged.
 */
function transformCamelToSnake(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj !== "object") return obj;
  // Don't transform Date objects or other special objects
  if (obj instanceof Date) return obj;
  if (Array.isArray(obj)) {
    return obj.map(transformCamelToSnake);
  }
  
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    // If key already contains underscore, assume it's already snake_case
    // Otherwise, convert camelCase to snake_case
    let snakeKey: string;
    if (key.includes('_')) {
      snakeKey = key; // Already snake_case
    } else {
      // Convert camelCase to snake_case
      // Pattern: [A-Z] -> _[lowercase]
      snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    }
    result[snakeKey] = transformCamelToSnake(value);
  }
  return result;
}

/**
 * Retrieves the JWT access token from localStorage.
 *
 * @returns The stored token or null if not found/not in browser
 * @internal
 */
function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("access_token");
}

/**
 * Stores a JWT access token in localStorage.
 *
 * Called after successful login to persist the authentication token.
 * The token will be automatically included in subsequent API requests.
 *
 * @param token - The JWT access token to store
 *
 * @example
 * const { data } = await api.post('/auth/login', credentials);
 * if (data) {
 *   setAuthToken(data.access_token);
 * }
 */
export function setAuthToken(token: string): void {
  if (typeof window !== "undefined") {
    localStorage.setItem("access_token", token);
  }
}

/**
 * Removes the JWT access token from localStorage.
 *
 * Called during logout or when a 401 response is received.
 * Subsequent API requests will not include an auth header.
 *
 * @example
 * // Manual logout
 * removeAuthToken();
 * router.push('/login');
 */
export function removeAuthToken(): void {
  if (typeof window !== "undefined") {
    localStorage.removeItem("access_token");
  }
}

/**
 * Core fetch wrapper with error handling and authentication.
 *
 * Handles all HTTP requests to the backend API with:
 *   - Automatic JSON serialization/deserialization
 *   - JWT token injection when auth is enabled
 *   - Automatic redirect to login on 401 responses
 *   - Normalized error handling
 *
 * @template T - Expected response data type
 * @param endpoint - API endpoint path (e.g., '/auth/me')
 * @param options - Request configuration options
 * @returns Promise resolving to ApiResponse with data or error
 *
 * @example
 * const response = await fetchApi<User>('/auth/me');
 * if (response.error) {
 *   console.error('Failed:', response.error);
 * } else {
 *   console.log('User:', response.data);
 * }
 *
 * @internal
 */
async function fetchApi<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<ApiResponse<T>> {
  const { method = "GET", body, headers = {}, auth = true, signal } = options;

  const isFormData = body instanceof FormData;
  const requestHeaders: Record<string, string> = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...headers,
  };

  // Add auth token if available and auth is enabled
  if (auth) {
    const token = getAuthToken();
    if (token) {
      requestHeaders["Authorization"] = `Bearer ${token}`;
      if (import.meta.env.DEV) {
        logger.debug("[API] Sending authenticated request to", endpoint);
      }
    } else {
      if (import.meta.env.DEV) {
        logger.warn("[API] No auth token available for authenticated request to", endpoint);
      }
    }
  }

  const fullUrl = `${getApiBaseUrl()}${endpoint}`;

  if (import.meta.env.DEV) {
    logger.debug("[API]", method, fullUrl);
  }

  try {
    // Transform request body from camelCase to snake_case
    // Always transform to ensure consistency - backend always expects snake_case
    let requestBody = body;
    if (body && typeof body === 'object' && !Array.isArray(body) && !(body instanceof Date) && !(body instanceof FormData)) {
      // Transform camelCase to snake_case for backend
      requestBody = transformCamelToSnake(body);
    }

    
    const response = await fetch(fullUrl, {
      method,
      headers: requestHeaders,
      body: isFormData ? (requestBody as FormData) : requestBody ? JSON.stringify(requestBody) : undefined,
      signal,
    });

    // Handle non-JSON responses (including 204 No Content)
    const contentType = response.headers.get("content-type");
    let data: T | null = null;

    // 204 No Content has no body, skip JSON parsing
    if (response.status === 204) {
      data = null;
    } else if (contentType?.includes("application/json")) {
      const jsonData = await response.json();
      // Transform snake_case to camelCase for frontend consistency
      // Always transform to ensure consistency - backend always returns snake_case
      if (jsonData !== null && typeof jsonData === 'object') {
        data = transformSnakeToCamel(jsonData) as T;
      } else {
        data = jsonData as T;
      }
    }

    if (!response.ok) {
      // Handle 401 Unauthorized - clear token
      // Note: We don't redirect here anymore - let the auth context and layouts handle it
      // This prevents full page reloads and redirect loops
      if (response.status === 401 && auth) {
        // Only clear token if we're not in the middle of an auth operation
        // The auth context and protected layouts will handle the redirect
        if (!isAuthOperationInProgress && !isAuthLoading) {
          removeAuthToken();
          // Notify auth context so it clears user state and layout can redirect to login
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
          }
        }
      }

      // Handle FastAPI validation errors - detail can be a string or array of validation errors
      let errorMessage: string;
      const errorData = data as { detail?: string | Array<{ msg?: string; loc?: unknown[] }> };
      if (errorData?.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Format validation errors: "field: message" or just "message"
          errorMessage = errorData.detail
            .map((err) => {
              const field = Array.isArray(err.loc) && err.loc.length > 0 
                ? err.loc[err.loc.length - 1] 
                : null;
              const msg = err.msg || 'Validation error';
              return field ? `${field}: ${msg}` : msg;
            })
            .join(', ');
        } else {
          errorMessage = `HTTP error ${response.status}`;
        }
      } else {
        errorMessage = `HTTP error ${response.status}`;
      }

      // 4xx = client error (e.g. 409 already in queue); 5xx = server error
      const isServerError = response.status >= 500;
      if (isServerError) {
        logger.error("[API] Request failed:", { fullUrl, status: response.status, error: errorMessage });
      } else {
        logger.warn("[API] Request failed:", { fullUrl, status: response.status, error: errorMessage });
      }
      return {
        data: data as T | null,
        error: errorMessage,
        status: response.status,
        responseHeaders: pickRelevantResponseHeaders(response),
      };
    }


    return {
      data,
      error: null,
      status: response.status,
      responseHeaders: pickRelevantResponseHeaders(response),
    };
  } catch (error) {
    logger.error("[API] Network error:", fullUrl, error);
    return {
      data: null,
      error: error instanceof Error ? error.message : "Network error",
      status: 0,
    };
  }
}

/**
 * API client with typed HTTP method helpers.
 *
 * Provides convenient methods for each HTTP verb with proper typing.
 * All methods automatically handle authentication unless explicitly disabled.
 *
 * @example
 * // GET request (auth defaults to true)
 * const { data } = await api.get<User>('/auth/me');
 *
 * // POST request without auth (public endpoint)
 * const { data } = await api.post<AuthResponse>(
 *   '/auth/login',
 *   { email, password },
 *   { auth: false }
 * );
 *
 * // PUT request to update a resource
 * const { data } = await api.put<PrayerRequest>(
 *   `/prayer-requests/${id}`,
 *   { body: 'Updated text' }
 * );
 *
 * // DELETE request
 * const { error } = await api.delete(`/prayer-requests/${id}`);
 */
export const api = {
  /**
   * Performs a GET request.
   * @template T - Expected response data type
   * @param endpoint - API endpoint path
   * @param options - Request options (excluding method and body)
   */
  get: <T>(endpoint: string, options?: Omit<RequestOptions, "method" | "body">) =>
    fetchApi<T>(endpoint, { ...options, method: "GET" }),

  /**
   * Performs a POST request.
   * @template T - Expected response data type
   * @param endpoint - API endpoint path
   * @param body - Request body data
   * @param options - Request options (excluding method)
   */
  post: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, "method">) =>
    fetchApi<T>(endpoint, { ...options, method: "POST", body }),

  /**
   * Performs a PUT request.
   * @template T - Expected response data type
   * @param endpoint - API endpoint path
   * @param body - Request body data
   * @param options - Request options (excluding method)
   */
  put: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, "method">) =>
    fetchApi<T>(endpoint, { ...options, method: "PUT", body }),

  /**
   * Performs a PATCH request.
   * @template T - Expected response data type
   * @param endpoint - API endpoint path
   * @param body - Partial update data
   * @param options - Request options (excluding method)
   */
  patch: <T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, "method">) =>
    fetchApi<T>(endpoint, { ...options, method: "PATCH", body }),

  /**
   * Performs a DELETE request.
   * @template T - Expected response data type
   * @param endpoint - API endpoint path
   * @param options - Request options (excluding method and body)
   */
  delete: <T>(endpoint: string, options?: Omit<RequestOptions, "method" | "body">) =>
    fetchApi<T>(endpoint, { ...options, method: "DELETE" }),
};

/** Response shape for GET /production/live-prayer-request (keys may be camelCase after transform). */
export interface LivePrayerContentResponse {
  content: {
    prayer_request?: string;
    prayerRequest?: string;
    submitter: string;
    location: string;
  };
}

/**
 * Fetches the currently set live prayer request (for display devices).
 * Returns 404 when no prayer is set live.
 */
export async function getLivePrayerRequest(): Promise<{
  data: LivePrayerContentResponse | null;
  error: string | null;
  status: number;
}> {
  return api.get<LivePrayerContentResponse>("/production/live-prayer-request", {
    auth: false,
  });
}

/**
 * Sets the prayer request to display as live. Requires producer or admin.
 */
export async function setLivePrayerRequest(prayerRequestId: string): Promise<{
  data: { success: boolean } | null;
  error: string | null;
  status: number;
}> {
  return api.post<{ success: boolean }>("/production/live-prayer-request", {
    prayer_request_id: prayerRequestId,
  });
}

/**
 * Sets the answered prayer to display as live. Requires producer or admin.
 */
export async function setLivePraiseRequest(prayerRequestId: string): Promise<{
  data: { success: boolean } | null;
  error: string | null;
  status: number;
}> {
  return api.post<{ success: boolean }>("/production/live-praise-report", {
    prayer_request_id: prayerRequestId,
  });
}

/** Server-persisted producer shuffle state (camelCase). */
export interface ShuffleStateResponse {
  prayerEnabled: boolean;
  praiseEnabled: boolean;
  combinedEnabled: boolean;
  intervalSeconds: number;
  prayerIntervalSeconds: number;
  praiseIntervalSeconds: number;
  combinedIntervalSeconds: number;
}

/** Partial update for PUT /production/shuffle-state. */
export interface ShuffleStateUpdate {
  prayerEnabled?: boolean;
  praiseEnabled?: boolean;
  combinedEnabled?: boolean;
  intervalSeconds?: number;
  prayerIntervalSeconds?: number;
  praiseIntervalSeconds?: number;
  combinedIntervalSeconds?: number;
}

/** Allowed shuffle interval values in seconds (15s, 30s, 1m, 2m, 5m). */
export const SHUFFLE_INTERVAL_OPTIONS = [15, 30, 60, 120, 300] as const;

/** One upcoming combined shuffle pick. */
export interface CombinedNextPreview {
  id: string;
  queueType: 'prayer' | 'praise' | string;
}

/** Current live IDs and shuffle-next from GET /production/current-live. */
export interface CurrentLiveResponse {
  livePrayerRequestId: string | null;
  livePraiseRequestId: string | null;
  liveCombinedRequestId?: string | null;
  liveCombinedQueueType?: string | null;
  shufflePrayerNextRequestId: string | null;
  shufflePraiseNextRequestId: string | null;
  shufflePrayerNextIds?: string[];
  shufflePraiseNextIds?: string[];
  shuffleCombinedNextIds?: CombinedNextPreview[];
}

/**
 * Set the combined channel live item (fans out to appropriate enabled shuffles).
 */
export async function setLiveCombined(prayerRequestId: string): Promise<{
  data: { success: boolean } | null;
  error: string | null;
  status: number;
}> {
  return api.post<{ success: boolean }>("/production/live-combined", {
    prayer_request_id: prayerRequestId,
  });
}

/**
 * Get which prayer/praise is currently on display. Producer or admin. Use on producer page load so Live badge shows immediately.
 */
export async function getCurrentLive(): Promise<{
  data: CurrentLiveResponse | null;
  error: string | null;
  status: number;
}> {
  return api.get<CurrentLiveResponse>("/production/current-live");
}

/**
 * Get current producer shuffle state (persists across refresh/logout). Producer or admin.
 */
export async function getShuffleState(): Promise<{
  data: ShuffleStateResponse | null;
  error: string | null;
  status: number;
}> {
  return api.get<ShuffleStateResponse>("/production/shuffle-state");
}

/**
 * Update producer shuffle state. Broadcasts to all producers. Producer or admin.
 */
export async function putShuffleState(update: ShuffleStateUpdate): Promise<{
  data: ShuffleStateResponse | null;
  error: string | null;
  status: number;
}> {
  return api.put<ShuffleStateResponse>("/production/shuffle-state", update);
}

// =============================================================================
// Simulation (practice mode) - Admin only
// =============================================================================

export interface SimulationStatusResponse {
  running: boolean;
  startedAt: string | null;
}

export interface SimulationStartParams {
  intervalMinSec: number;
  intervalMaxSec: number;
  durationMin?: number;
}

export interface SimulationStartResponse {
  running: boolean;
  startedAt: string;
}

export interface SimulationClearResponse {
  deletedCount: number;
}

export async function getSimulationStatus(): Promise<{
  data: SimulationStatusResponse | null;
  error: string | null;
  status: number;
}> {
  return api.get<SimulationStatusResponse>("/simulation/status");
}

export async function startSimulation(params: SimulationStartParams): Promise<{
  data: SimulationStartResponse | null;
  error: string | null;
  status: number;
}> {
  return api.post<SimulationStartResponse>("/simulation/start", params);
}

export async function stopSimulation(): Promise<{
  data: { running: boolean } | null;
  error: string | null;
  status: number;
}> {
  return api.post<{ running: boolean }>("/simulation/stop");
}

export async function clearSimulationData(): Promise<{
  data: SimulationClearResponse | null;
  error: string | null;
  status: number;
}> {
  return api.post<SimulationClearResponse>("/simulation/clear");
}

/**
 * Creates a WebSocket connection to the One Voice backend.
 *
 * Establishes a real-time connection for receiving live updates about:
 *   - New prayer submissions
 *   - Prayer status changes (approved, displayed, etc.)
 *   - Queue updates for producers
 *
 * The WebSocket automatically handles JSON parsing for incoming messages.
 *
 * @param onMessage - Callback for incoming messages (already parsed if JSON)
 * @param onError - Optional callback for connection errors
 * @param onClose - Optional callback when connection closes
 * @returns WebSocket instance or null if not in browser
 *
 * @example
 * const ws = createWebSocket(
 *   (data) => {
 *     if (data.event === 'new_prayer') {
 *       setPrayers(prev => [data.data, ...prev]);
 *     }
 *   },
 *   (error) => console.error('WS error:', error),
 *   () => console.log('WS disconnected')
 * );
 *
 * // Clean up on unmount
 * return () => ws?.close();
 */
export function createWebSocket(
  onMessage: (data: unknown) => void,
  onError?: (error: Event) => void,
  onClose?: () => void
): WebSocket | null {
  if (typeof window === "undefined") return null;

  const defaultWsBase =
    typeof window !== "undefined" &&
    window.location?.hostname &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1"
      ? `ws://${window.location.hostname}:8000`
      : "ws://localhost:8000";

  const wsUrl = import.meta.env.VITE_WS_URL || `${defaultWsBase}/ws`;
  const ws = new WebSocket(wsUrl);

  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      // Transform snake_case to camelCase for WebSocket messages too
      const transformed = transformSnakeToCamel(data);
      onMessage(transformed);
    } catch {
      onMessage(event.data);
    }
  };

  ws.onerror = (error) => {
    logger.error("WebSocket error:", error);
    onError?.(error);
  };

  ws.onclose = () => {
    logger.info("WebSocket connection closed");
    onClose?.();
  };

  return ws;
}
